import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, IndianRupee, AlertTriangle } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_PROJECT_ROI_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, EmptyState, StatTile, money, num, resolveDateRange,
    StatusFilterSelect,
} from "./adReportKit";

export default function AdProjectRoiReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_roi_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_roi_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_roi_to", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_roi_status", "");
    const [attributedOnly, setAttributedOnly] = usePersistedState("ad_roi_attributed", false);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (statusFilter) p.append("status", statusFilter);
        if (attributedOnly) p.append("attributed_only", "true");

        fetch(`${AD_PROJECT_ROI_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load ROI report");
            })
            .catch(() => ToastMsg("error", "Failed to load ROI report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, attributedOnly, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const s = data?.summary;
    const rows = data?.data || [];
    const maxSpend = Math.max(...rows.map((r) => Math.max(r.spend, r.revenue)), 1);

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <IndianRupee className="h-6 w-6 text-primary" />
                            Marketing ROI
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Meta spend beside closed revenue, per product.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo }}
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <label className="flex items-center gap-2 text-xs text-muted-foreground h-9 px-2 rounded-md border border-input bg-background cursor-pointer">
                        <input
                            type="checkbox"
                            checked={attributedOnly}
                            onChange={(e) => setAttributedOnly(e.target.checked)}
                            className="rounded border-input"
                        />
                        Attributed leads only
                    </label>
                </AdFilterBar>

                {/* Filtering spend by delivery status while blended revenue stays
                    whole overstates ROAS. Say so loudly rather than let the
                    number be read as real. */}
                {data?.status_filter_note && (
                    <div className="flex items-start gap-2 rounded-lg p-3 text-xs bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span><strong>ROAS not comparable — </strong>{data.status_filter_note}</span>
                    </div>
                )}

                {/* The single most important caveat on this page, stated where it
                    is read rather than buried in a tooltip. */}
                {data?.revenue_basis && (
                    <div className={cn(
                        "flex items-start gap-2 rounded-lg p-3 text-xs",
                        data.revenue_basis === "blended"
                            ? "bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300"
                            : "bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300",
                    )}>
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            <strong>
                                {data.revenue_basis === "blended" ? "Blended ROI" : "Attributed ROI"}
                                {" — "}
                            </strong>
                            {data.revenue_basis_note}
                        </span>
                    </div>
                )}

                {s && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile tone="spend" label="Meta spend" value={money(s.spend)} />
                        <StatTile tone="leads" label="Revenue" value={money(s.revenue)} />
                        <StatTile
                            tone={s.profit >= 0 ? "leads" : "bad"}
                            label={s.profit >= 0 ? "Surplus" : "Shortfall"}
                            value={money(Math.abs(s.profit))} />
                        <StatTile label="ROAS"
                            value={s.roas == null ? "—" : `${Number(s.roas).toFixed(2)}×`} />
                        <StatTile label="Deals won" value={num(s.won)} />
                        <StatTile tone="cost" label="CAC" value={money(s.cac)} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && rows.length === 0 && (
                    <EmptyState title="Nothing to compare in this period"
                        hint="No Meta spend and no closed deals fell in this date range." />
                )}

                {!loading && rows.length > 0 && (
                    <>
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                                Spend vs revenue
                            </p>
                            {rows.map((r) => (
                                <div key={r.project_id ?? "none"} className="space-y-1">
                                    <div className="flex items-baseline justify-between gap-2 text-xs">
                                        <span className="font-medium text-foreground truncate">{r.project_name}</span>
                                        <span className="text-muted-foreground tabular-nums shrink-0">
                                            {money(r.spend)} spend · {money(r.revenue)} revenue
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="h-2 rounded-sm bg-blue-500"
                                            style={{ width: `${(r.spend * 100) / maxSpend}%`, minWidth: r.spend > 0 ? 2 : 0 }}
                                            title={`Spend ${money(r.spend)}`} />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <div className="h-2 rounded-sm bg-emerald-500"
                                            style={{ width: `${(r.revenue * 100) / maxSpend}%`, minWidth: r.revenue > 0 ? 2 : 0 }}
                                            title={`Revenue ${money(r.revenue)}`} />
                                    </div>
                                </div>
                            ))}
                            <div className="flex gap-4 pt-1 text-[10px] text-muted-foreground">
                                <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm bg-blue-500" />Spend</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm bg-emerald-500" />Revenue</span>
                            </div>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium">Project</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Campaigns</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Spend</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Meta leads</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CPL</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CRM leads</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Demos</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Won</th>
                                        <th className="px-3 py-2.5 text-right font-medium">CAC</th>
                                        <th className="px-3 py-2.5 text-right font-medium">Revenue</th>
                                        <th className="px-3 py-2.5 text-right font-medium">ROAS</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rows.map((r) => (
                                        <tr key={r.project_id ?? "none"} className="hover:bg-accent/30">
                                            <td className="px-3 py-2 font-medium text-foreground">{r.project_name}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(r.campaigns)}</td>
                                            <td className="px-3 py-2 text-right font-medium text-foreground tabular-nums">{money(r.spend)}</td>
                                            <td className="px-3 py-2 text-right text-foreground tabular-nums">{num(r.meta_leads)}</td>
                                            <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400 tabular-nums">{money(r.cpl)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(r.crm_leads)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(r.demos)}</td>
                                            <td className="px-3 py-2 text-right text-foreground font-medium tabular-nums">{num(r.won)}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{money(r.cac)}</td>
                                            <td className="px-3 py-2 text-right text-foreground tabular-nums">{money(r.revenue)}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">
                                                {r.roas == null ? (
                                                    <span className="text-muted-foreground">—</span>
                                                ) : (
                                                    <span className={cn("font-semibold",
                                                        r.roas >= 1
                                                            ? "text-emerald-600 dark:text-emerald-400"
                                                            : "text-red-600 dark:text-red-400")}>
                                                        {Number(r.roas).toFixed(2)}×
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <p className="text-[11px] text-muted-foreground">
                            Campaigns roll up to their own project when one is set, otherwise to their ad
                            account's. Anything unmapped appears as <strong className="text-foreground">Unassigned</strong> —
                            fix that under Settings → Meta Ads → Map campaigns.
                        </p>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
